import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  Box,
  Chip,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
} from '@material-ui/core';
import {
  Warning as WarningIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { teal, green } from '@material-ui/core/colors';

import toastError from '../../errors/toastError';
import { toast } from 'react-toastify';
import api from '../../services/api';
import OrderModal from '../orderModal';
import FileLinksModal from '../FileLinksModal';
import GoogleScheduleModal from '../GoogleScheduleModal';

const useStyles = makeStyles(theme => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  categoryTitle: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  activityField: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  requiredField: {
    '& .MuiInputLabel-root': {
      '&::after': {
        content: '" *"',
        color: theme.palette.error.main,
      },
    },
  },
  saveButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  warningBox: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const PreRequirementsModal = ({
  open,
  onClose,
  dealId,
  targetStageId,
  onRequirementsFilled,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [preRequirements, setPreRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  // Estados para controlar abertura dos modais
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [googleScheduleModalOpen, setGoogleScheduleModalOpen] = useState(false);
  const [fileLinksModalOpen, setFileLinksModalOpen] = useState(false);
  const [dealData, setDealData] = useState(null);
  const isLoadingRef = useRef(false);

  // Carregar dados do deal
  const loadDealData = useCallback(async () => {
    if (!dealId) return null;

    try {
      const { data } = await api.get(`/deals/${dealId}`);
      setDealData(data);
      return data;
    } catch (err) {
      console.warn('Erro ao carregar dados do deal:', err);
      return null;
    }
  }, [dealId]);

  // Carregar pré-requisitos do deal
  const loadPreRequirements = useCallback(async (dealDataToUse = null) => {
    if (!dealId) return;
    
    // Evitar múltiplas chamadas simultâneas
    if (isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;

    try {
      setLoading(true);
      
      // Sempre buscar dados do deal se não foram passados como parâmetro
      let currentDealData = dealDataToUse;
      if (!currentDealData) {
        const { data } = await api.get(`/deals/${dealId}`);
        currentDealData = data;
        // Atualizar estado apenas uma vez
        setDealData(data);
      }
      
      // Buscar pipelineId do deal
      const pipelineId = currentDealData.pipelineId || currentDealData.pipeline?.id;
      if (!pipelineId) {
        setPreRequirements([]);
        setLoading(false);
        return;
      }

      // Buscar pré-requisitos configurados para "Perdidos" na pipeline
      const { data: preReqData } = await api.get(
        `/pipelines/${pipelineId}/lost-stage-pre-requirements`
      );
      const configuredPreRequirements = preReqData.preRequirements || [];

      // Buscar requirements do deal (sempre recarregar para ter dados atualizados)
      const { data: requirementsData } = await api.get(
        `/deals/${dealId}/requirements`
      );
      const dealRequirements = requirementsData.requirements || [];

      // Criar um mapa dos requirements do deal por ID
      const dealRequirementsMap = new Map(
        dealRequirements.map(req => [req.id, req])
      );

      // Montar lista de TODOS os pré-requisitos configurados encontrados no deal
      const allPreRequirements = configuredPreRequirements
        .map(preReq => {
          const requirement =
            preReq.requirement || dealRequirementsMap.get(preReq.requirementId);
          if (!requirement) return null;
          return requirement;
        })
        .filter(req => req !== null);

      // Identificar apenas os obrigatórios que ainda NÃO estão preenchidos (para logs/contagem)
      const unmetPreRequirements = allPreRequirements.filter(req => {
        if (!req.required) return false;

        // Verificar se está preenchido - verificar tanto filled quanto value
        const isFilled =
          req.filled ||
          (req.value !== null &&
            req.value !== undefined &&
            req.value !== '' &&
            req.value !== false);

        return !isFilled;
      });

      // Converter TODOS os pré-requisitos configurados em atividades (obrigatórios e opcionais)
      const activities = allPreRequirements.map(req => {
        let activity = {
          id: req.id,
          descricao: req.name,
          obrigatorio: req.required,
          order: req.order,
          filled: req.filled || false,
          value: req.value || (req.type === 'checkbox' ? false : ''),
          type: req.type,
        };

        // Mapear tipos de requirements para tipos de atividades
        switch (req.type) {
          case 'checkbox':
            activity.tipo_gatilho = 'checkbox';
            break;
          case 'text':
            activity.tipo_gatilho = 'input';
            break;
          case 'select':
            activity.tipo_gatilho = 'select';
            activity.opcoes = req.options || [];
            break;
          case 'date':
            activity.tipo_gatilho = 'date';
            break;
          case 'sale':
            activity.tipo_gatilho = 'indicator';
            activity.descricao = `${req.name} - Venda realizada`;
            break;
          case 'attach':
            activity.tipo_gatilho = 'indicator';
            activity.descricao = `${req.name} - Links anexados`;
            break;
          case 'schedule':
            activity.tipo_gatilho = 'indicator';
            activity.descricao = `${req.name} - Data agendada`;
            break;
          default:
            activity.tipo_gatilho = 'input';
        }

        return activity;
      });

      // Ordenar por ordem definida
      activities.sort((a, b) => a.order - b.order);

      setPreRequirements(activities);
      
      console.log('Pré-requisitos carregados:', {
        total: configuredPreRequirements.length,
        obrigatorios: configuredPreRequirements.filter(pr => pr.requirement?.required || pr.required).length,
        naoPreenchidos: unmetPreRequirements.length,
        atividades: activities.length
      });
    } catch (err) {
      console.warn('Erro ao carregar pré-requisitos:', err);
      toastError(err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [dealId]);

  useEffect(() => {
    if (open && dealId) {
      // Resetar estados quando o modal abre
      setPreRequirements([]);
      setPendingChanges({});
      setDealData(null);
      isLoadingRef.current = false;
      
      // Carregar dados do deal primeiro, depois carregar pré-requisitos
      const loadData = async () => {
        const loadedDealData = await loadDealData();
        if (loadedDealData && !isLoadingRef.current) {
          // Passar os dados diretamente para evitar dependência do estado
          await loadPreRequirements(loadedDealData);
        }
      };
      
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId]);

  const handleActivityChange = (activityId, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [activityId]: value,
    }));
  };

  const handleSave = async () => {
    if (!dealId) return;

    try {
      setSaving(true);

      // Validar se todos os campos obrigatórios foram preenchidos
      const requiredActivities = preRequirements.filter(a => a.obrigatorio);
      const filledActivities = requiredActivities.filter(
        activity =>
          pendingChanges[activity.id] !== undefined &&
          pendingChanges[activity.id] !== '' &&
          pendingChanges[activity.id] !== null &&
          pendingChanges[activity.id] !== false
      );

      if (filledActivities.length < requiredActivities.length) {
        toast.error(
          'Por favor, preencha todos os campos obrigatórios antes de continuar.'
        );
        setSaving(false);
        return;
      }

      // Salvar cada mudança e coletar erros
      const saveErrors = [];
      const savePromises = [];
      
      // Garantir que salvamos todos os requirements que foram alterados
      for (const [activityId, value] of Object.entries(pendingChanges)) {
        // Processar o valor baseado no tipo da atividade
        const activity = preRequirements.find(a => a.id.toString() === activityId);
        let processedValue = value;
        
        // Ajustar o valor conforme o tipo
        if (activity?.tipo_gatilho === 'checkbox') {
          processedValue = typeof value === 'boolean' ? value : Boolean(value);
        } else if (activity?.tipo_gatilho === 'date' && value) {
          // Garantir que datas estejam no formato correto
          processedValue = value instanceof Date ? value.toISOString() : value;
        } else if (value === null || value === undefined) {
          // Não salvar valores nulos ou indefinidos
          continue;
        }
        
        // Criar promise para salvar este requirement
        const savePromise = api
          .post(`/deals/${dealId}/requirement/${activityId}`, {
            value: processedValue,
          })
          .then(() => {
            console.log(
              `Requirement ${activityId} (${activity?.descricao || activityId}) salvo com sucesso com valor:`,
              processedValue
            );
            return { success: true, activityId, activityName: activity?.descricao || activityId };
          })
          .catch(err => {
            console.error(`Erro ao salvar requirement ${activityId}:`, err);
            const activityName = activity?.descricao || activityId;
            return {
              success: false,
              activityId,
              activityName,
              error: err.response?.data?.message || err.message || 'Erro desconhecido',
            };
          });
        
        savePromises.push(savePromise);
      }
      
      // Aguardar todas as requisições serem concluídas
      const results = await Promise.all(savePromises);
      
      // Separar sucessos e erros
      const errors = results.filter(r => !r.success);
      if (errors.length > 0) {
        saveErrors.push(...errors);
      }

      // Se houver erros ao salvar, mostrar mensagem
      if (saveErrors.length > 0) {
        const errorMessages = saveErrors
          .map(e => `${e.activityName}: ${e.error}`)
          .join('\n');
        toast.error(
          `Erro ao salvar alguns pré-requisitos:\n${errorMessages}`,
          {
            autoClose: 5000,
          }
        );
        setSaving(false);
        return;
      }

      // Se todas as requisições foram bem-sucedidas (200), fechar modal e mover para o estágio
      // Limpar mudanças pendentes após salvar com sucesso
      setPendingChanges({});

      toast.success('Pré-requisitos preenchidos com sucesso!');

      // Fechar o modal
      onClose();

      // Fazer a requisição para mover para o estágio "Perdidos"
      if (onRequirementsFilled && targetStageId) {
        console.log(
          'Todas as requirements foram salvas com sucesso (200), movendo deal para estágio:',
          targetStageId
        );
        // Chamar callback para mover o deal
        onRequirementsFilled(dealId, targetStageId);
      }
    } catch (error) {
      console.error('Erro ao salvar pré-requisitos:', error);
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenOrderModal = () => {
    setOrderModalOpen(true);
  };

  const handleCloseOrderModal = async () => {
    setOrderModalOpen(false);
    await loadPreRequirements();
  };

  const handleOpenGoogleScheduleModal = () => {
    setGoogleScheduleModalOpen(true);
  };

  const handleCloseGoogleScheduleModal = async () => {
    setGoogleScheduleModalOpen(false);
    await loadPreRequirements();
  };

  const handleOpenFileLinksModal = () => {
    if (!dealId) {
      toast.error('Nenhum negócio vinculado.');
      return;
    }
    setFileLinksModalOpen(true);
  };

  const handleCloseFileLinksModal = async () => {
    setFileLinksModalOpen(false);
    await loadPreRequirements();
  };

  // Função para detectar se uma atividade é de um tipo específico
  const getActivityType = activity => {
    const description = activity.descricao?.toLowerCase() || '';

    if (
      description.includes('venda') ||
      description.includes('realizar venda') ||
      description.includes('vendas')
    ) {
      return 'vendas';
    }
    if (
      description.includes('agendamento') ||
      description.includes('agendar') ||
      description.includes('agenda')
    ) {
      return 'agendamento';
    }
    if (
      description.includes('anexo') ||
      description.includes('arquivo') ||
      description.includes('documento')
    ) {
      return 'anexos';
    }

    return 'default';
  };

  // Função para renderizar botões específicos de atividades
  const renderActivityButtons = activity => {
    const activityType = getActivityType(activity);
    const isCompleted =
      activity.filled ||
      (activity.value && activity.value !== '' && activity.value !== false);

    switch (activityType) {
      case 'vendas':
        return (
          <Box display='flex' gap={1} mt={1}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleOpenOrderModal}
            >
              Realizar Venda
            </Button>
          </Box>
        );

      case 'agendamento':
        return (
          <Box display='flex' gap={1} mt={1}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleOpenGoogleScheduleModal}
            >
              Agendar
            </Button>
          </Box>
        );

      case 'anexos':
        return (
          <Box display='flex' gap={1} mt={1}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleOpenFileLinksModal}
            >
              Anexar Arquivos
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  // Função para renderizar diferentes tipos de atividades
  const renderActivityInput = activity => {
    const currentValue =
      pendingChanges[activity.id] !== undefined
        ? pendingChanges[activity.id]
        : activity.value || '';
    const isRequired = activity.obrigatorio;
    const isEmpty = !currentValue || currentValue === '';
    const hasError = isRequired && isEmpty;

    const getLabel = baseLabel => {
      return isRequired ? `${baseLabel} *` : baseLabel;
    };

    switch (activity.tipo_gatilho) {
      case 'checkbox':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentValue === true || currentValue === 'true'}
                  onChange={e =>
                    handleActivityChange(activity.id, e.target.checked)
                  }
                  color='primary'
                />
              }
              label={getLabel(activity.descricao)}
            />
            <Typography
              variant='caption'
              color='textSecondary'
              display='block'
              style={{ marginTop: 4 }}
            >
              {activity.obrigatorio
                ? 'Campo obrigatório - Marque o checkbox para a resposta "Sim"'
                : 'Marque o checkbox para a resposta "Sim"'}
            </Typography>
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'select':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <FormControl fullWidth error={hasError}>
              <InputLabel>{getLabel(activity.descricao)}</InputLabel>
              <Select
                value={currentValue}
                onChange={e =>
                  handleActivityChange(activity.id, e.target.value)
                }
              >
                {activity.opcoes?.map((opcao, index) => (
                  <MenuItem key={index} value={opcao}>
                    {opcao}
                  </MenuItem>
                ))}
              </Select>
              {hasError && (
                <Typography variant='caption' color='error'>
                  Este campo é obrigatório
                </Typography>
              )}
            </FormControl>
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'date':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <TextField
              label={getLabel(activity.descricao)}
              type='date'
              value={currentValue || ''}
              onChange={e => handleActivityChange(activity.id, e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              error={hasError}
              helperText={hasError ? 'Este campo é obrigatório' : ''}
            />
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'input':
      default:
        return (
          <Box key={activity.id} className={classes.activityField}>
            <TextField
              label={getLabel(activity.descricao)}
              value={currentValue || ''}
              onChange={e => handleActivityChange(activity.id, e.target.value)}
              fullWidth
              multiline
              rows={3}
              error={hasError}
              helperText={hasError ? 'Este campo é obrigatório' : ''}
            />
            {renderActivityButtons(activity)}
          </Box>
        );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box className={classes.title}>
            <WarningIcon style={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Typography variant='h6'>
              Pré-requisitos Obrigatórios para "Perdidos"
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box className={classes.warningBox}>
            <Typography variant='body1' gutterBottom>
              <strong>Atenção!</strong> Você precisa preencher os seguintes
              pré-requisitos obrigatórios antes de mover este deal para o
              estágio "Perdidos":
            </Typography>
          </Box>

          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : preRequirements.length === 0 ? (
            <Box textAlign='center' py={4}>
              <CheckCircleIcon
                style={{ fontSize: 48, color: green[500], marginBottom: 16 }}
              />
              <Typography variant='body1' color='textSecondary'>
                Todos os pré-requisitos obrigatórios já foram preenchidos!
              </Typography>
            </Box>
          ) : (
            <Box>
              {preRequirements.map(activity => renderActivityInput(activity))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading || preRequirements.length === 0}
          >
            {saving ? 'Salvando...' : 'Salvar e Continuar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modais auxiliares */}
      {dealData && (
        <>
          <OrderModal
            open={orderModalOpen}
            onClose={handleCloseOrderModal}
            contactId={dealData.contact?.id}
            ticketId={dealData.ticket?.id || dealData.tickets?.[0]?.id}
            dealId={dealId}
            onSaleSuccess={() => {
              setGoogleScheduleModalOpen(true);
            }}
            reload={async () => {
              await loadPreRequirements();
            }}
          />
          <GoogleScheduleModal
            open={googleScheduleModalOpen}
            onClose={handleCloseGoogleScheduleModal}
            contactId={dealData.contact?.id}
            ticket={dealData.ticket || dealData.tickets?.[0]}
            dealId={dealId}
            contact={dealData.contact}
            reload={async () => {
              await loadPreRequirements();
            }}
          />
          <FileLinksModal
            open={fileLinksModalOpen}
            onClose={handleCloseFileLinksModal}
            ticket={dealData.ticket || dealData.tickets?.[0]}
            dealId={dealData.id}
            reload={async () => {
              await loadPreRequirements();
            }}
          />
        </>
      )}
    </>
  );
};

export default PreRequirementsModal;
